import { HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, forkJoin, map, Observable, of, timeout } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { DiningTableBulkRequest } from "../../../services/generated/erp-api/models/dining-table-bulk-request";
import { DiningTableDto } from "../../../services/generated/erp-api/models/dining-table-dto";
import { DiningTableApiService } from "../../../services/generated/erp-api/services/dining-table-api.service";
import { CompanyApiService } from "../../../services/generated/waba-api/services/company-api.service";
import { CompanyPhonesService } from "../../../shared/services/company-phones.service";
import { SKIP_ERROR_ALERT } from "../../../shared/interceptors/skip-error-alert.context";
import { isApiResponseOk } from "../../sale-points/sale-point.util";

/** What the QR codes of the tables need besides the tables: the shop name, its WhatsApp numbers and its logo. */
export interface DiningTableShop {
  name: string;
  phoneNumbers: string[];
  logoUrl?: string;
}

/**
 * Thin wrapper over the generated dining-table client: adds the request headers and
 * turns an ApiResponse whose `status` is not "200" into an error. The global error
 * toast is skipped: the Tables pages show their own message (the server's one when
 * it sends a readable one, e.g. a duplicate table name).
 */
@Injectable({ providedIn: "root" })
export class DiningTableService {
  constructor(
    private api: DiningTableApiService,
    private companyApi: CompanyApiService,
    private companyPhones: CompanyPhonesService,
  ) {}

  private readonly silent = new HttpContext().set(SKIP_ERROR_ALERT, true);

  private headers() {
    return { "x-api-canal": "web", "x-api-requestId": uuidv4() };
  }

  private unwrap<T>(response: { status?: string; body?: T }): T {
    if (!isApiResponseOk(response) || response.body == null) throw response;
    return response.body;
  }

  /** Already sorted by the server (position, then name). */
  list(): Observable<DiningTableDto[]> {
    return this.api.listDiningTables(this.headers(), this.silent).pipe(
      map((response) => {
        if (!isApiResponseOk(response)) throw response;
        return response.body || [];
      }),
    );
  }

  create(table: DiningTableDto): Observable<DiningTableDto> {
    return this.api.createDiningTable({ ...this.headers(), body: table }, this.silent).pipe(map((response) => this.unwrap(response)));
  }

  bulkCreate(request: DiningTableBulkRequest): Observable<DiningTableDto[]> {
    return this.api.bulkCreateDiningTables({ ...this.headers(), body: request }, this.silent).pipe(map((response) => this.unwrap(response)));
  }

  update(reference: string, table: DiningTableDto): Observable<DiningTableDto> {
    return this.api.updateDiningTable({ ...this.headers(), reference, body: table }, this.silent).pipe(map((response) => this.unwrap(response)));
  }

  regenerateQr(reference: string): Observable<DiningTableDto> {
    return this.api.regenerateDiningTableQr({ ...this.headers(), reference }, this.silent).pipe(map((response) => this.unwrap(response)));
  }

  delete(reference: string): Observable<void> {
    return this.api.deleteDiningTable({ ...this.headers(), reference }, this.silent);
  }

  /**
   * Shop name, WhatsApp numbers (the QR links point at the storefront of a number) and logo.
   * Never fails: a missing logo or name only makes the QR codes plainer.
   */
  shop(): Observable<DiningTableShop> {
    return forkJoin({
      company: this.companyApi.getCompanyByCompanyId(this.headers(), this.silent).pipe(
        map((response) => response.body),
        catchError(() => of(undefined)),
      ),
      phones: this.companyPhones.list(),
      logo: this.companyApi.getCompanyLogo(undefined, this.silent).pipe(
        timeout(30_000),
        map((response) => logoUrlOf(response)),
        catchError(() => of(undefined)),
      ),
    }).pipe(
      map(({ company, phones, logo }) => ({
        name: company?.name?.trim() || "",
        phoneNumbers: phones.map((phone) => phone.phoneNumber?.trim() || "").filter(Boolean),
        logoUrl: logo,
      })),
    );
  }
}

/** The logo endpoint answers with the image itself (a Blob) or with its URL. */
function logoUrlOf(response: unknown): string | undefined {
  if (response instanceof Blob && response.size > 0) return URL.createObjectURL(response);
  if (typeof response === "string" && response.trim()) return response.trim();
  return undefined;
}
